import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Clock, Navigation } from "lucide-react";

const haversineKm = (a, b) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export const DeliveryMap = ({ location, path, destination }) => {
  const elRef = useRef();
  const mapRef = useRef();
  const markerRef = useRef();
  const destRef = useRef();
  const lineRef = useRef();
  const destLineRef = useRef();

  useEffect(() => {
    if (!location || !elRef.current) return;
    const pos = [location.lat, location.lng];
    if (!mapRef.current) {
      mapRef.current = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView(pos, 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(mapRef.current);
      const icon = L.divIcon({
        className: "",
        html: '<div style="width:22px;height:22px;border-radius:9999px;background:#EA580C;border:3px solid #fff;box-shadow:0 0 0 4px rgba(234,88,12,.3)"></div>',
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      markerRef.current = L.marker(pos, { icon }).addTo(mapRef.current);
    } else {
      markerRef.current.setLatLng(pos);
    }

    if (path && path.length > 1) {
      const latlngs = path.map((p) => [p.lat, p.lng]);
      if (lineRef.current) lineRef.current.setLatLngs(latlngs);
      else lineRef.current = L.polyline(latlngs, { color: "#065F46", weight: 4, opacity: 0.7 }).addTo(mapRef.current);
    }

    if (destination) {
      const dpos = [destination.lat, destination.lng];
      const dicon = L.divIcon({
        className: "",
        html: '<div style="width:16px;height:16px;background:#065F46;border:3px solid #fff;border-radius:3px;transform:rotate(45deg);box-shadow:0 1px 5px rgba(0,0,0,.35)"></div>',
        iconSize: [20, 20], iconAnchor: [10, 10],
      });
      if (destRef.current) destRef.current.setLatLng(dpos);
      else destRef.current = L.marker(dpos, { icon: dicon, title: "Destination" }).addTo(mapRef.current);

      const dl = [pos, dpos];
      if (destLineRef.current) destLineRef.current.setLatLngs(dl);
      else destLineRef.current = L.polyline(dl, { color: "#EA580C", weight: 3, opacity: 0.6, dashArray: "6 8" }).addTo(mapRef.current);

      const km = haversineKm(location, destination);
      let zoom = 14;
      if (km > 0.6) zoom = 13;
      if (km > 1.5) zoom = 12;
      if (km > 4) zoom = 11;
      if (km > 9) zoom = 10;
      const center = [(location.lat + destination.lat) / 2, (location.lng + destination.lng) / 2];
      const applyView = () => {
        if (!mapRef.current) return;
        mapRef.current.invalidateSize();
        mapRef.current.setView(center, zoom);
      };
      applyView();
      requestAnimationFrame(applyView);
      setTimeout(applyView, 250);
    } else {
      const applyView = () => { if (mapRef.current) { mapRef.current.invalidateSize(); mapRef.current.setView(pos, 14); } };
      applyView();
      setTimeout(applyView, 200);
    }
  }, [location, path, destination]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  if (!location) return null;

  let eta = null;
  if (destination) {
    const km = haversineKm(location, destination);
    const mins = Math.max(1, Math.round((km / 25) * 60));
    eta = { km: km.toFixed(1), mins };
  }

  return (
    <div>
      {eta && (
        <div data-testid="delivery-eta" className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
          <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-800"><Clock className="w-4 h-4" />~{eta.mins} min</span>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-stone-600"><Navigation className="w-4 h-4 text-mango-600" />{eta.km} km</span>
        </div>
      )}
      <div ref={elRef} data-testid="delivery-map" style={{ height: 240 }} className="rounded-xl overflow-hidden border border-stone-200 z-0" />
      {destination && (
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-stone-400">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-mango-600" />Livreur</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-emerald-800 rotate-45" />Destination</span>
        </div>
      )}
    </div>
  );
};
